<!--
  管理端「工作区浏览」面板：工作区 → 工作表 → 时间块 → 评论 四级只读下钻。

  ⚠️ 管理端没有 vue-router，标签页是 AdminPage 的本地 state，所以这里用
     内部 view 状态机（workspaces / projects / timeblocks / comments）而非路由，
     面包屑负责「我在哪一层、怎么回去」。

  写操作只有评论的「下架 / 恢复」，直接复用内容审核接口（audit/hide、audit/restore，
  resourceType=SHARED_COMMENT）——不新造轮子，审核留痕天然生效。

  入口联动：AdminPage 通过 preset prop 传入「从某个用户详情跳进来」的 userId；
  反向跳用户详情通过 $emit('view-user') 交回 AdminPage 处理。
-->
<template>
  <div>
    <!-- ------------------------------ 面包屑 ------------------------------ -->
    <div class="wsp-crumb">
      <template v-for="(c, i) in crumbs">
        <span v-if="i > 0" :key="'sep' + i" class="adm-crumb-sep">/</span>
        <span
          v-if="i === crumbs.length - 1"
          :key="'cur' + i"
          class="adm-crumb-current"
        >{{ c.label }}</span>
        <span v-else :key="'lk' + i" class="wsp-crumb-link" @click="goToView(c.view)">{{ c.label }}</span>
      </template>
    </div>

    <!-- ------------------------------ 第 1 层：工作区列表 ------------------------------ -->
    <div v-if="view === 'workspaces'" class="adm-card">
      <div class="adm-card-head">
        <div class="adm-card-title">
          <span class="adm-card-title-bar" />工作区列表
          <span class="adm-card-title-extra">共 {{ wsTotal }} 个</span>
        </div>
      </div>

      <div class="adm-filter">
        <el-input
          v-model="wsQuery.keyword"
          class="wsp-w220"
          name="wsKeyword"
          autocomplete="off"
          prefix-icon="el-icon-search"
          placeholder="工作区名称 / ID"
          clearable
          @keyup.enter.native="onWsSearch"
        />
        <el-input
          v-model="wsQuery.userId"
          class="wsp-w150"
          name="wsUserId"
          autocomplete="off"
          placeholder="归属用户 ID"
          clearable
          @keyup.enter.native="onWsSearch"
        />
        <el-select v-model="wsQuery.type" class="wsp-w120" placeholder="全部类型" clearable>
          <el-option label="个人" value="PERSONAL" />
          <el-option label="团队" value="TEAM" />
          <el-option label="企业" value="ENTERPRISE" />
        </el-select>
        <el-select v-model="wsQuery.status" class="wsp-w120" placeholder="全部状态" clearable>
          <el-option label="启用" value="ACTIVE" />
          <el-option label="归档" value="ARCHIVED" />
          <el-option label="已删除" value="DELETED" />
        </el-select>
        <el-checkbox v-model="wsQuery.includeDeleted">包含已删除</el-checkbox>
        <el-button type="primary" icon="el-icon-search" :loading="wsLoading" @click="onWsSearch">
          查询
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="onWsReset">重置</el-button>
      </div>

      <el-table v-loading="wsLoading" :data="wsRows" border>
        <el-table-column prop="id" label="工作区 ID" width="180">
          <template slot-scope="{ row }">
            <span class="adm-code">{{ row.id }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="name" label="名称" min-width="140" show-overflow-tooltip />
        <el-table-column label="归属用户" min-width="150">
          <template slot-scope="{ row }">
            <el-link type="primary" :underline="false" @click="$emit('view-user', Number(row.ownerId))">
              {{ row.ownerNickname || '-' }}
            </el-link>
            <span class="adm-sub">（{{ row.ownerId }}）</span>
          </template>
        </el-table-column>
        <el-table-column label="类型" width="90">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="typeTagType(row.type)">{{ typeText(row.type) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="90">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="row.deleted ? 'danger' : 'success'">
              {{ row.deleted ? '已删除' : statusText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="工作表" width="80">
          <template slot-scope="{ row }">
            <span class="adm-num">{{ row.projectCount }}</span>
          </template>
        </el-table-column>
        <el-table-column label="时间块" width="80">
          <template slot-scope="{ row }">
            <span class="adm-num">{{ row.timeBlockCount }}</span>
          </template>
        </el-table-column>
        <el-table-column label="创建时间" width="150">
          <template slot-scope="{ row }">{{ formatTime(row.createdAt) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="100" fixed="right">
          <template slot-scope="{ row }">
            <div class="adm-op-actions">
              <el-link type="primary" :underline="false" @click="enterProjects(row)">查看工作表</el-link>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <AdmPager
        :total="wsTotal"
        :page="wsQuery.page"
        :size="wsQuery.size"
        @page-change="onWsPageChange"
        @size-change="onWsSizeChange"
      />
    </div>

    <!-- ------------------------------ 第 2 层：工作区摘要 + 工作表列表 ------------------------------ -->
    <div v-else-if="view === 'projects' && workspace" class="adm-card">
      <div class="adm-desc">
        <div class="adm-desc-item">
          <span class="adm-desc-label">工作区 ID</span>
          <span class="adm-desc-value is-mono">{{ workspace.id }}</span>
        </div>
        <div class="adm-desc-item">
          <span class="adm-desc-label">归属用户</span>
          <span class="adm-desc-value">
            <el-link type="primary" :underline="false" @click="$emit('view-user', Number(workspace.ownerId))">
              {{ workspace.ownerNickname || '-' }}
            </el-link>
            <span class="adm-sub">（{{ workspace.ownerId }}）</span>
          </span>
        </div>
        <div class="adm-desc-item">
          <span class="adm-desc-label">类型</span>
          <span class="adm-desc-value">{{ typeText(workspace.type) }}</span>
        </div>
        <div class="adm-desc-item">
          <span class="adm-desc-label">状态</span>
          <span class="adm-desc-value">
            <el-tag size="mini" :type="workspace.deleted ? 'danger' : 'success'">
              {{ workspace.deleted ? '已删除' : statusText(workspace.status) }}
            </el-tag>
          </span>
        </div>
        <div class="adm-desc-item">
          <span class="adm-desc-label">时区</span>
          <span class="adm-desc-value">{{ workspace.timezone || '-' }}</span>
        </div>
        <div class="adm-desc-item">
          <span class="adm-desc-label">分享可见性</span>
          <span class="adm-desc-value">
            <el-tag v-if="workspace.shareVisibility" size="mini" type="warning">
              {{ visibilityText(workspace.shareVisibility) }}
            </el-tag>
            <span v-else class="adm-sub">未分享</span>
          </span>
        </div>
        <div class="adm-desc-item is-full">
          <span class="adm-desc-label">描述</span>
          <span class="adm-desc-value">{{ workspace.description || '-' }}</span>
        </div>
      </div>

      <div class="adm-stat-grid is-compact">
        <div class="adm-stat">
          <div class="adm-stat-label">工作表</div>
          <div class="adm-stat-value adm-num">{{ workspace.projectCount }}</div>
        </div>
        <div class="adm-stat">
          <div class="adm-stat-label">时间块</div>
          <div class="adm-stat-value adm-num">{{ workspace.timeBlockCount }}</div>
        </div>
        <div class="adm-stat">
          <div class="adm-stat-label">工作区评论</div>
          <div class="adm-stat-value adm-num">{{ workspace.commentCount }}</div>
        </div>
      </div>

      <div class="adm-section-title wsp-section-row">
        <span>工作表列表</span>
        <el-link
          type="primary"
          :underline="false"
          @click="openComments('WORKSPACE', workspace.id, workspace.name, 'projects')"
        >
          查看工作区评论（{{ workspace.commentCount }}）
        </el-link>
      </div>

      <div class="adm-filter">
        <el-input
          v-model="pjQuery.keyword"
          class="wsp-w220"
          name="pjKeyword"
          autocomplete="off"
          prefix-icon="el-icon-search"
          placeholder="工作表名称"
          clearable
          @keyup.enter.native="onPjSearch"
        />
        <el-checkbox v-model="pjQuery.includeDeleted">包含已删除</el-checkbox>
        <el-button type="primary" icon="el-icon-search" :loading="pjLoading" @click="onPjSearch">
          查询
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="onPjReset">重置</el-button>
      </div>

      <el-table v-loading="pjLoading" :data="pjRows" border>
        <el-table-column prop="id" label="工作表 ID" width="180">
          <template slot-scope="{ row }">
            <span class="adm-code">{{ row.id }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="name" label="名称" min-width="140" show-overflow-tooltip />
        <el-table-column label="状态" width="90">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="row.deleted ? 'danger' : 'success'">
              {{ row.deleted ? '已删除' : statusText(row.status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="时间块" width="80">
          <template slot-scope="{ row }">
            <span class="adm-num">{{ row.timeBlockCount }}</span>
          </template>
        </el-table-column>
        <el-table-column label="任务" width="80">
          <template slot-scope="{ row }">
            <span class="adm-num">{{ row.taskCount }}</span>
          </template>
        </el-table-column>
        <el-table-column label="创建时间" width="150">
          <template slot-scope="{ row }">{{ formatTime(row.createdAt) }}</template>
        </el-table-column>
        <el-table-column label="操作" width="100" fixed="right">
          <template slot-scope="{ row }">
            <div class="adm-op-actions">
              <el-link type="primary" :underline="false" @click="enterTimeBlocks(row)">查看时间块</el-link>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <AdmPager
        :total="pjTotal"
        :page="pjQuery.page"
        :size="pjQuery.size"
        @page-change="onPjPageChange"
        @size-change="onPjSizeChange"
      />
    </div>

    <!-- ------------------------------ 第 3 层：工作表摘要 + 时间块列表 ------------------------------ -->
    <div v-else-if="view === 'timeblocks' && project" class="adm-card">
      <div class="adm-desc">
        <div class="adm-desc-item">
          <span class="adm-desc-label">工作表 ID</span>
          <span class="adm-desc-value is-mono">{{ project.id }}</span>
        </div>
        <div class="adm-desc-item">
          <span class="adm-desc-label">所属工作区</span>
          <span class="adm-desc-value">{{ workspace && workspace.name }}（{{ project.workspaceId }}）</span>
        </div>
        <div class="adm-desc-item">
          <span class="adm-desc-label">状态</span>
          <span class="adm-desc-value">
            <el-tag size="mini" :type="project.deleted ? 'danger' : 'success'">
              {{ project.deleted ? '已删除' : statusText(project.status) }}
            </el-tag>
          </span>
        </div>
        <div class="adm-desc-item">
          <span class="adm-desc-label">分享可见性</span>
          <span class="adm-desc-value">
            <el-tag v-if="project.shareVisibility" size="mini" type="warning">
              {{ visibilityText(project.shareVisibility) }}
            </el-tag>
            <span v-else class="adm-sub">未分享</span>
          </span>
        </div>
        <div class="adm-desc-item is-full">
          <span class="adm-desc-label">描述</span>
          <span class="adm-desc-value">{{ project.description || '-' }}</span>
        </div>
      </div>

      <div class="adm-stat-grid is-compact">
        <div class="adm-stat">
          <div class="adm-stat-label">时间块</div>
          <div class="adm-stat-value adm-num">{{ project.timeBlockCount }}</div>
        </div>
        <div class="adm-stat">
          <div class="adm-stat-label">任务</div>
          <div class="adm-stat-value adm-num">{{ project.taskCount }}</div>
        </div>
        <div class="adm-stat">
          <div class="adm-stat-label">工作表评论</div>
          <div class="adm-stat-value adm-num">{{ project.commentCount }}</div>
        </div>
      </div>

      <div class="adm-section-title wsp-section-row">
        <span>时间块列表</span>
        <el-link
          type="primary"
          :underline="false"
          @click="openComments('PROJECT', project.id, project.name, 'timeblocks')"
        >
          查看项目评论（{{ project.commentCount }}）
        </el-link>
      </div>

      <div class="adm-filter">
        <el-input
          v-model="tbQuery.keyword"
          class="wsp-w220"
          name="tbKeyword"
          autocomplete="off"
          prefix-icon="el-icon-search"
          placeholder="内容 / 备注"
          clearable
          @keyup.enter.native="onTbSearch"
        />
        <el-select v-model="tbQuery.executionStatus" class="wsp-w120" placeholder="全部状态" clearable>
          <el-option v-for="s in tbStatusOptions" :key="s.value" :label="s.label" :value="s.value" />
        </el-select>
        <el-date-picker
          v-model="tbQuery.dateRange"
          class="wsp-w260"
          type="daterange"
          range-separator="~"
          start-placeholder="计划开始起"
          end-placeholder="计划开始止"
          value-format="yyyy-MM-dd"
          clearable
        />
        <el-checkbox v-model="tbQuery.includeDeleted">包含已删除</el-checkbox>
        <el-button type="primary" icon="el-icon-search" :loading="tbLoading" @click="onTbSearch">
          查询
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="onTbReset">重置</el-button>
      </div>

      <el-table v-loading="tbLoading" :data="tbRows" border>
        <el-table-column prop="id" label="时间块 ID" width="170">
          <template slot-scope="{ row }">
            <span class="adm-code">{{ row.id }}</span>
          </template>
        </el-table-column>
        <el-table-column label="内容" min-width="160" show-overflow-tooltip>
          <template slot-scope="{ row }">
            <span>{{ row.content || '（无内容）' }}</span>
            <span v-if="row.allDay" class="adm-sub">全天</span>
          </template>
        </el-table-column>
        <el-table-column label="类型" width="80">
          <template slot-scope="{ row }">{{ tbTypeText(row.blockType) }}</template>
        </el-table-column>
        <el-table-column label="计划 / 实际" width="220">
          <template slot-scope="{ row }">
            <div>{{ formatTime(row.plannedStartTime) }} ~ {{ formatTime(row.plannedEndTime) }}</div>
            <div class="adm-sub">
              {{ row.actualStartTime ? formatTime(row.actualStartTime) + ' ~ ' + formatTime(row.actualEndTime) : '未执行' }}
            </div>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="90">
          <template slot-scope="{ row }">
            <el-tag size="mini" :type="tbStatusTagType(row.executionStatus)">
              {{ tbStatusText(row.executionStatus) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="预计/实际(分)" width="120">
          <template slot-scope="{ row }">
            <span class="adm-num">{{ row.estimatedMinutes }}</span>
            <span class="adm-sub"> / {{ row.actualMinutes }}</span>
          </template>
        </el-table-column>
        <el-table-column label="创建人" width="150">
          <template slot-scope="{ row }">
            <span class="adm-code">{{ row.createdBy }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="130" fixed="right">
          <template slot-scope="{ row }">
            <div class="adm-op-actions">
              <el-link type="primary" :underline="false" @click="openTbDetail(row)">详情</el-link>
              <el-link
                type="primary"
                :underline="false"
                @click="openComments('TIMEBLOCK', row.id, row.content || '时间块 ' + row.id, 'timeblocks')"
              >
                评论{{ row.commentCount ? '（' + row.commentCount + '）' : '' }}
              </el-link>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <AdmPager
        :total="tbTotal"
        :page="tbQuery.page"
        :size="tbQuery.size"
        @page-change="onTbPageChange"
        @size-change="onTbSizeChange"
      />
    </div>

    <!-- ------------------------------ 第 4 层：评论列表 ------------------------------ -->
    <div v-else-if="view === 'comments'" class="adm-card">
      <div class="adm-desc">
        <template v-if="!cmByUser">
          <div class="adm-desc-item">
            <span class="adm-desc-label">资源类型</span>
            <span class="adm-desc-value">{{ resourceTypeText(commentCtx.resourceType) }}</span>
          </div>
          <div class="adm-desc-item">
            <span class="adm-desc-label">资源 ID</span>
            <span class="adm-desc-value is-mono">{{ commentCtx.resourceId }}</span>
          </div>
          <div class="adm-desc-item is-full">
            <span class="adm-desc-label">资源</span>
            <span class="adm-desc-value">{{ commentCtx.label }}</span>
          </div>
        </template>
        <template v-else>
          <div class="adm-desc-item">
            <span class="adm-desc-label">搜索范围</span>
            <span class="adm-desc-value">全部资源 · 评论人 {{ cmQuery.userId }}</span>
          </div>
          <div class="adm-desc-item is-full">
            <span class="adm-desc-label">提示</span>
            <span class="adm-desc-value">正在按评论人全局搜索（含各工作区 / 工作表 / 时间块）；清空「评论人 ID」后恢复当前资源范围</span>
          </div>
        </template>
      </div>

      <div class="adm-filter">
        <el-input
          v-model="cmQuery.keyword"
          class="wsp-w220"
          name="cmKeyword"
          autocomplete="off"
          prefix-icon="el-icon-search"
          placeholder="评论内容"
          clearable
          @keyup.enter.native="onCmSearch"
        />
        <el-input
          v-model="cmQuery.userId"
          class="wsp-w150"
          name="cmUserId"
          autocomplete="off"
          placeholder="评论人 ID（全局）"
          clearable
          @keyup.enter.native="onCmSearch"
        />
        <el-checkbox v-model="cmQuery.includeDeleted">包含已删除</el-checkbox>
        <el-button type="primary" icon="el-icon-search" :loading="cmLoading" @click="onCmSearch">
          查询
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="onCmReset">重置</el-button>
      </div>

      <el-table v-loading="cmLoading" :data="cmRows" border>
        <el-table-column prop="id" label="评论 ID" width="170">
          <template slot-scope="{ row }">
            <span class="adm-code">{{ row.id }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="content" label="内容" min-width="220" show-overflow-tooltip />
        <el-table-column label="评论人" min-width="140">
          <template slot-scope="{ row }">
            <el-link type="primary" :underline="false" @click="filterByCommenter(row.userId)">
              {{ row.userNickname || '-' }}
            </el-link>
            <span class="adm-sub">（{{ row.userId }}）</span>
          </template>
        </el-table-column>
        <el-table-column label="回复给" width="130">
          <template slot-scope="{ row }">
            <span v-if="row.parentId">{{ row.replyToNickname || row.replyToUserId || '-' }}</span>
            <span v-else class="adm-sub">-</span>
          </template>
        </el-table-column>
        <el-table-column label="资源所有者" min-width="140">
          <template slot-scope="{ row }">
            <span>{{ row.ownerNickname || '-' }}</span>
            <span class="adm-sub">（{{ row.ownerId }}）</span>
          </template>
        </el-table-column>
        <el-table-column label="时间" width="150">
          <template slot-scope="{ row }">{{ formatTime(row.createdAt) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="90">
          <template slot-scope="{ row }">
            <el-tag v-if="!row.deleted" size="mini" type="success">正常</el-tag>
            <el-tag v-else-if="row.lastAuditAction === 'HIDE'" size="mini" type="danger">已下架</el-tag>
            <el-tag v-else size="mini" type="info">已删除</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="90" fixed="right">
          <template slot-scope="{ row }">
            <div class="adm-op-actions">
              <el-link
                v-if="!row.deleted"
                type="danger"
                :underline="false"
                @click="openAction(row, 'hide')"
              >下架</el-link>
              <el-link
                v-else-if="row.lastAuditAction === 'HIDE'"
                type="primary"
                :underline="false"
                @click="openAction(row, 'restore')"
              >恢复</el-link>
              <span v-else class="adm-sub">-</span>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <AdmPager
        :total="cmTotal"
        :page="cmQuery.page"
        :size="cmQuery.size"
        @page-change="onCmPageChange"
        @size-change="onCmSizeChange"
      />

      <div class="adm-inline-hint">
        「已删除」是用户自己删的，无法恢复；「已下架」是管理员下架的，可恢复。
      </div>
    </div>

    <!-- ------------------------------ 时间块详情 ------------------------------ -->
    <el-dialog title="时间块详情" :visible.sync="tbDetailVisible" width="640px" append-to-body>
      <div v-if="tbDetailRow" class="adm-desc">
        <div class="adm-desc-item">
          <span class="adm-desc-label">时间块 ID</span>
          <span class="adm-desc-value is-mono">{{ tbDetailRow.id }}</span>
        </div>
        <div class="adm-desc-item">
          <span class="adm-desc-label">类型</span>
          <span class="adm-desc-value">{{ tbTypeText(tbDetailRow.blockType) }}</span>
        </div>
        <div class="adm-desc-item">
          <span class="adm-desc-label">执行状态</span>
          <span class="adm-desc-value">
            <el-tag size="mini" :type="tbStatusTagType(tbDetailRow.executionStatus)">
              {{ tbStatusText(tbDetailRow.executionStatus) }}
            </el-tag>
          </span>
        </div>
        <div class="adm-desc-item">
          <span class="adm-desc-label">全天</span>
          <span class="adm-desc-value">{{ tbDetailRow.allDay ? '是' : '否' }}</span>
        </div>
        <div class="adm-desc-item is-full">
          <span class="adm-desc-label">内容</span>
          <span class="adm-desc-value">{{ tbDetailRow.content || '（无内容）' }}</span>
        </div>
        <div class="adm-desc-item is-full">
          <span class="adm-desc-label">备注</span>
          <span class="adm-desc-value">{{ tbDetailRow.note || '-' }}</span>
        </div>
        <div class="adm-desc-item">
          <span class="adm-desc-label">计划开始</span>
          <span class="adm-desc-value">{{ formatTime(tbDetailRow.plannedStartTime) }}</span>
        </div>
        <div class="adm-desc-item">
          <span class="adm-desc-label">计划结束</span>
          <span class="adm-desc-value">{{ formatTime(tbDetailRow.plannedEndTime) }}</span>
        </div>
        <div class="adm-desc-item">
          <span class="adm-desc-label">实际开始</span>
          <span class="adm-desc-value">{{ formatTime(tbDetailRow.actualStartTime) }}</span>
        </div>
        <div class="adm-desc-item">
          <span class="adm-desc-label">实际结束</span>
          <span class="adm-desc-value">{{ formatTime(tbDetailRow.actualEndTime) }}</span>
        </div>
        <div class="adm-desc-item">
          <span class="adm-desc-label">预计 / 实际（分）</span>
          <span class="adm-desc-value">
            <span class="adm-num">{{ tbDetailRow.estimatedMinutes }}</span>
            <span class="adm-sub"> / {{ tbDetailRow.actualMinutes }}</span>
          </span>
        </div>
        <div class="adm-desc-item">
          <span class="adm-desc-label">删除状态</span>
          <span class="adm-desc-value">
            <el-tag v-if="tbDetailRow.deleted" size="mini" type="danger">已删除</el-tag>
            <el-tag v-else size="mini" type="success">正常</el-tag>
          </span>
        </div>
        <div class="adm-desc-item">
          <span class="adm-desc-label">所属工作区</span>
          <span class="adm-desc-value is-mono">{{ tbDetailRow.workspaceId }}</span>
        </div>
        <div class="adm-desc-item">
          <span class="adm-desc-label">所属工作表</span>
          <span class="adm-desc-value is-mono">{{ tbDetailRow.projectId }}</span>
        </div>
        <div class="adm-desc-item">
          <span class="adm-desc-label">关联任务</span>
          <span class="adm-desc-value is-mono">{{ tbDetailRow.taskId || '-' }}</span>
        </div>
        <div class="adm-desc-item">
          <span class="adm-desc-label">关联标签</span>
          <span class="adm-desc-value is-mono">{{ tbDetailRow.tagId || '-' }}</span>
        </div>
        <div class="adm-desc-item">
          <span class="adm-desc-label">创建人</span>
          <span class="adm-desc-value is-mono">{{ tbDetailRow.createdBy }}</span>
        </div>
        <div class="adm-desc-item">
          <span class="adm-desc-label">创建时间</span>
          <span class="adm-desc-value">{{ formatTime(tbDetailRow.createdAt) }}</span>
        </div>
      </div>
      <div slot="footer" class="wsp-dialog-footer">
        <el-link
          type="primary"
          :underline="false"
          @click="viewTbComments"
        >查看评论（{{ tbDetailRow && tbDetailRow.commentCount }}）</el-link>
        <el-button size="small" @click="tbDetailVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- ------------------------------ 下架 / 恢复评论 ------------------------------ -->
    <el-dialog
      :title="actionMode === 'hide' ? '下架评论' : '恢复评论'"
      :visible.sync="actionVisible"
      width="520px"
      append-to-body
    >
      <div class="adm-desc">
        <div class="adm-desc-item is-full">
          <span class="adm-desc-label">评论内容</span>
          <span class="adm-desc-value">{{ actionRow && actionRow.content }}</span>
        </div>
        <div class="adm-desc-item is-full">
          <span class="adm-desc-label">评论人</span>
          <span class="adm-desc-value">
            {{ actionRow && actionRow.userNickname }}（{{ actionRow && actionRow.userId }}）
          </span>
        </div>
      </div>

      <div class="adm-field-label">{{ actionMode === 'hide' ? '下架原因' : '恢复原因' }}</div>
      <el-input
        v-model="actionReason"
        name="reason"
        autocomplete="off"
        type="textarea"
        :rows="3"
        maxlength="200"
        show-word-limit
        placeholder="请填写原因（必填，会留痕）"
      />
      <div class="adm-inline-hint">复用内容审核链路，操作会写入审核流水与审计日志。</div>

      <div slot="footer">
        <el-button size="small" @click="actionVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="actionLoading" @click="submitAction">
          确定
        </el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  fetchWorkspaces,
  fetchWorkspaceDetail,
  fetchWorkspaceProjects,
  fetchProjectDetail,
  fetchProjectTimeBlocks,
  fetchComments,
  hideContent,
  restoreContent,
  isOk
} from '@/api/admin'
import AdmPager from '@/components/admin/AdmPager.vue'

const WS_TYPE_TEXT = { PERSONAL: '个人', TEAM: '团队', ENTERPRISE: '企业' }
const STATUS_TEXT = { ACTIVE: '启用', ARCHIVED: '归档', DELETED: '已删除' }
const TB_TYPE_TEXT = { TASK: '任务', EVENT: '日程', BREAK: '休息', FOCUS: '专注' }
const TB_STATUS_TEXT = {
  PLANNED: '计划',
  RUNNING: '进行中',
  PAUSED: '暂停',
  DONE: '已完成',
  COMPLETED: '已完成',
  MISSED: '已错过',
  SKIPPED: '已跳过',
  CANCELLED: '已取消'
}
const RESOURCE_TYPE_TEXT = { WORKSPACE: '工作区', PROJECT: '工作表', TIMEBLOCK: '时间块' }
const VISIBILITY_TEXT = { PRIVATE: '私有', FRIENDS: '好友可见', PUBLIC: '公开', INHERIT: '继承工作区' }

/** 状态下拉用的执行状态选项（口径与 DDL 注释一致：PLANNED/RUNNING/PAUSED/DONE/MISSED/CANCELLED） */
const TB_STATUS_OPTIONS = [
  { value: 'PLANNED', label: '计划' },
  { value: 'RUNNING', label: '进行中' },
  { value: 'PAUSED', label: '暂停' },
  { value: 'DONE', label: '已完成' },
  { value: 'MISSED', label: '已错过' },
  { value: 'CANCELLED', label: '已取消' }
]

export default {
  name: 'WorkspacePanel',
  components: { AdmPager },
  props: {
    /**
     * 从用户详情跳转时带入的初始过滤：{ userId, ts }。
     * ts 保证同一用户两次进入也能触发 watch（对象引用不同）。
     */
    preset: { type: Object, default: null }
  },
  data() {
    return {
      view: 'workspaces',
      workspace: null,
      project: null,
      commentCtx: { resourceType: '', resourceId: null, label: '', fromView: 'projects' },
      // 模块常量挂 data 才能被模板访问（Vue 2 模板作用域不含模块级 const）
      tbStatusOptions: TB_STATUS_OPTIONS,
      wsQuery: { keyword: '', userId: '', type: '', status: '', includeDeleted: false, page: 1, size: 20 },
      wsRows: [],
      wsTotal: 0,
      wsLoading: false,
      pjQuery: { keyword: '', includeDeleted: false, page: 1, size: 20 },
      pjRows: [],
      pjTotal: 0,
      pjLoading: false,
      tbQuery: { keyword: '', executionStatus: '', dateRange: [], includeDeleted: false, page: 1, size: 20 },
      tbRows: [],
      tbTotal: 0,
      tbLoading: false,
      cmQuery: { keyword: '', userId: '', includeDeleted: false, page: 1, size: 20 },
      cmRows: [],
      cmTotal: 0,
      cmLoading: false,
      tbDetailVisible: false,
      tbDetailRow: null,
      actionVisible: false,
      actionLoading: false,
      actionMode: 'hide',
      actionRow: null,
      actionReason: ''
    }
  },
  computed: {
    /** 评论视图是否处于「按评论人全局搜索」模式（留空评论人 ID 即恢复当前资源范围） */
    cmByUser() {
      return this.toIdOrNull(this.cmQuery.userId) != null
    },
    crumbs() {
      const list = [{ label: '工作区浏览', view: 'workspaces' }]
      // 按评论人全局搜索时与具体资源解绑，面包屑只留根 + 评论，避免挂着失效的资源路径
      if (this.view === 'comments' && this.cmByUser) {
        list.push({ label: '评论（评论人 ' + this.cmQuery.userId + '）', view: 'comments' })
        return list
      }
      if (this.view !== 'workspaces' && this.workspace) {
        list.push({ label: this.workspace.name || '未命名工作区', view: 'projects' })
      }
      if (this.view === 'timeblocks' && this.project) {
        list.push({ label: this.project.name || '未命名工作表', view: 'timeblocks' })
      }
      if (this.view === 'comments') {
        // 从 timeblocks 视图进评论（项目级 / 时间块级）才带工作表一级
        if (this.commentCtx.fromView === 'timeblocks' && this.project) {
          list.push({ label: this.project.name || '未命名工作表', view: 'timeblocks' })
        }
        list.push({ label: '评论', view: 'comments' })
      }
      return list
    }
  },
  watch: {
    preset: {
      immediate: true,
      handler(val) {
        if (val && val.userId) {
          this.wsQuery.userId = String(val.userId)
          this.resetToWorkspaces()
          this.loadWorkspaces()
        }
      }
    }
  },
  created() {
    // preset 的 immediate watch 在 created 之前就跑过，带了 preset 就不再拉一次，避免重复请求
    if (!(this.preset && this.preset.userId)) {
      this.loadWorkspaces()
    }
  },
  methods: {
    // ---------------------------------------------------------------- 工作区
    loadWorkspaces() {
      this.wsLoading = true
      fetchWorkspaces({
        keyword: this.wsQuery.keyword,
        userId: this.toIdOrNull(this.wsQuery.userId),
        type: this.wsQuery.type,
        status: this.wsQuery.status,
        includeDeleted: this.wsQuery.includeDeleted,
        page: this.wsQuery.page,
        size: this.wsQuery.size
      })
        .then(res => {
          if (!isOk(res)) {
            throw new Error((res && res.message) || '查询失败')
          }
          this.wsRows = res.data.list || []
          // total 是 Long，后端序列化成字符串
          this.wsTotal = Number(res.data.total || 0)
        })
        .catch(err => {
          this.$message.error(err.message || '查询失败')
        })
        .finally(() => {
          this.wsLoading = false
        })
    },
    onWsSearch() {
      this.wsQuery.page = 1
      this.loadWorkspaces()
    },
    onWsReset() {
      this.wsQuery = { keyword: '', userId: '', type: '', status: '', includeDeleted: false, page: 1, size: 20 }
      this.loadWorkspaces()
    },
    onWsPageChange(page) {
      this.wsQuery.page = page
      this.loadWorkspaces()
    },
    onWsSizeChange(size) {
      this.wsQuery.size = size
      this.wsQuery.page = 1
      this.loadWorkspaces()
    },

    // ---------------------------------------------------------------- 工作表
    enterProjects(row) {
      fetchWorkspaceDetail(row.id)
        .then(res => {
          if (!isOk(res)) {
            throw new Error((res && res.message) || '获取工作区详情失败')
          }
          this.workspace = res.data
          this.project = null
          this.view = 'projects'
          this.pjQuery = { keyword: '', includeDeleted: false, page: 1, size: 20 }
          this.loadProjects()
        })
        .catch(err => {
          this.$message.error(err.message || '获取工作区详情失败')
        })
    },
    loadProjects() {
      if (!this.workspace) return
      this.pjLoading = true
      fetchWorkspaceProjects(this.workspace.id, {
        keyword: this.pjQuery.keyword,
        includeDeleted: this.pjQuery.includeDeleted,
        page: this.pjQuery.page,
        size: this.pjQuery.size
      })
        .then(res => {
          if (!isOk(res)) {
            throw new Error((res && res.message) || '查询失败')
          }
          this.pjRows = res.data.list || []
          this.pjTotal = Number(res.data.total || 0)
        })
        .catch(err => {
          this.$message.error(err.message || '查询失败')
        })
        .finally(() => {
          this.pjLoading = false
        })
    },
    onPjSearch() {
      this.pjQuery.page = 1
      this.loadProjects()
    },
    onPjReset() {
      this.pjQuery = { keyword: '', includeDeleted: false, page: 1, size: 20 }
      this.loadProjects()
    },
    onPjPageChange(page) {
      this.pjQuery.page = page
      this.loadProjects()
    },
    onPjSizeChange(size) {
      this.pjQuery.size = size
      this.pjQuery.page = 1
      this.loadProjects()
    },

    // ---------------------------------------------------------------- 时间块
    enterTimeBlocks(row) {
      fetchProjectDetail(row.id)
        .then(res => {
          if (!isOk(res)) {
            throw new Error((res && res.message) || '获取工作表详情失败')
          }
          this.project = res.data
          this.view = 'timeblocks'
          this.tbQuery = { keyword: '', executionStatus: '', dateRange: [], includeDeleted: false, page: 1, size: 20 }
          this.loadTimeBlocks()
        })
        .catch(err => {
          this.$message.error(err.message || '获取工作表详情失败')
        })
    },
    loadTimeBlocks() {
      if (!this.project) return
      this.tbLoading = true
      const range = this.tbQuery.dateRange || []
      fetchProjectTimeBlocks(this.project.id, {
        keyword: this.tbQuery.keyword,
        executionStatus: this.tbQuery.executionStatus,
        dateFrom: range[0] || '',
        dateTo: range[1] || '',
        includeDeleted: this.tbQuery.includeDeleted,
        page: this.tbQuery.page,
        size: this.tbQuery.size
      })
        .then(res => {
          if (!isOk(res)) {
            throw new Error((res && res.message) || '查询失败')
          }
          this.tbRows = res.data.list || []
          this.tbTotal = Number(res.data.total || 0)
        })
        .catch(err => {
          this.$message.error(err.message || '查询失败')
        })
        .finally(() => {
          this.tbLoading = false
        })
    },
    onTbSearch() {
      this.tbQuery.page = 1
      this.loadTimeBlocks()
    },
    onTbReset() {
      this.tbQuery = { keyword: '', executionStatus: '', dateRange: [], includeDeleted: false, page: 1, size: 20 }
      this.loadTimeBlocks()
    },
    onTbPageChange(page) {
      this.tbQuery.page = page
      this.loadTimeBlocks()
    },
    onTbSizeChange(size) {
      this.tbQuery.size = size
      this.tbQuery.page = 1
      this.loadTimeBlocks()
    },

    // ---------------------------------------------------------------- 评论
    openComments(resourceType, resourceId, label, fromView) {
      this.commentCtx = { resourceType, resourceId, label, fromView }
      // 从工作表视图看「工作区级评论」时清掉 project：面包屑不应多出一级
      if (fromView === 'projects') {
        this.project = null
      }
      this.view = 'comments'
      // 换资源 = 换范围，评论人过滤一并清掉（否则会残留成全局搜索，范围悄悄变了）
      this.cmQuery = { keyword: '', userId: '', includeDeleted: false, page: 1, size: 20 }
      this.loadComments()
    },
    loadComments() {
      this.cmLoading = true
      // 填了评论人 ID → 全局按评论人搜（后端 userId 单独传即跨资源）；
      // 留空 → 当前资源范围。两种模式共用一套表格。
      const byUser = this.toIdOrNull(this.cmQuery.userId)
      const params = byUser
        ? {
          userId: byUser,
          keyword: this.cmQuery.keyword,
          includeDeleted: this.cmQuery.includeDeleted,
          page: this.cmQuery.page,
          size: this.cmQuery.size
        }
        : {
          resourceType: this.commentCtx.resourceType,
          resourceId: this.commentCtx.resourceId,
          keyword: this.cmQuery.keyword,
          includeDeleted: this.cmQuery.includeDeleted,
          page: this.cmQuery.page,
          size: this.cmQuery.size
        }
      fetchComments(params)
        .then(res => {
          if (!isOk(res)) {
            throw new Error((res && res.message) || '查询失败')
          }
          this.cmRows = res.data.list || []
          this.cmTotal = Number(res.data.total || 0)
        })
        .catch(err => {
          this.$message.error(err.message || '查询失败')
        })
        .finally(() => {
          this.cmLoading = false
        })
    },
    onCmSearch() {
      this.cmQuery.page = 1
      this.loadComments()
    },
    onCmReset() {
      this.cmQuery = { keyword: '', userId: '', includeDeleted: false, page: 1, size: 20 }
      this.loadComments()
    },
    onCmPageChange(page) {
      this.cmQuery.page = page
      this.loadComments()
    },
    onCmSizeChange(size) {
      this.cmQuery.size = size
      this.cmQuery.page = 1
      this.loadComments()
    },
    openAction(row, mode) {
      this.actionRow = row
      this.actionMode = mode
      this.actionReason = ''
      this.actionVisible = true
    },
    submitAction() {
      if (this.actionLoading) return
      const reason = this.actionReason.trim()
      if (!reason) {
        this.$message.warning('请填写原因')
        return
      }
      this.actionLoading = true
      const job = this.actionMode === 'hide'
        ? hideContent({ resourceType: 'SHARED_COMMENT', resourceId: this.actionRow.id, reason })
        : restoreContent({ resourceType: 'SHARED_COMMENT', resourceId: this.actionRow.id, reason })
      job
        .then(res => {
          if (!isOk(res)) {
            throw new Error((res && res.message) || '操作失败')
          }
          this.$message.success(this.actionMode === 'hide' ? '已下架' : '已恢复')
          this.actionVisible = false
          // 宿主资源的评论计数会随下架/恢复变化，重新拉一次详情保持摘要数字诚实
          this.reloadCommentScopeCount()
          this.loadComments()
        })
        .catch(err => {
          this.$message.error(err.message || '操作失败')
        })
        .finally(() => {
          this.actionLoading = false
        })
    },
    /** 下架 / 恢复后刷新工作区或工作表摘要里的评论数（全局搜索模式与两者无关，跳过） */
    reloadCommentScopeCount() {
      if (this.cmByUser) return
      if (this.commentCtx.resourceType === 'WORKSPACE' && this.workspace) {
        fetchWorkspaceDetail(this.workspace.id)
          .then(res => {
            if (isOk(res)) this.workspace = res.data
          })
          .catch(() => {})
      } else if (this.commentCtx.resourceType === 'PROJECT' && this.project) {
        fetchProjectDetail(this.project.id)
          .then(res => {
            if (isOk(res)) this.project = res.data
          })
          .catch(() => {})
      }
    },
    /** 点评论人昵称 → 切成「该用户的全部评论」全局搜索 */
    filterByCommenter(userId) {
      this.cmQuery.userId = String(userId)
      this.cmQuery.page = 1
      this.loadComments()
    },
    /** 时间块详情弹窗（数据全在列表行里，不需要再请求） */
    openTbDetail(row) {
      this.tbDetailRow = row
      this.tbDetailVisible = true
    },
    viewTbComments() {
      const row = this.tbDetailRow
      this.tbDetailVisible = false
      this.openComments('TIMEBLOCK', row.id, row.content || '时间块 ' + row.id, 'timeblocks')
    },

    // ---------------------------------------------------------------- 导航
    goToView(view) {
      if (view === 'workspaces') {
        this.resetToWorkspaces()
      } else if (view === 'projects') {
        this.backToProjects()
      } else if (view === 'timeblocks') {
        this.backToTimeBlocks()
      }
    },
    resetToWorkspaces() {
      this.view = 'workspaces'
      this.workspace = null
      this.project = null
    },
    backToProjects() {
      if (!this.workspace) {
        this.resetToWorkspaces()
        return
      }
      this.view = 'projects'
      this.project = null
      this.loadProjects()
    },
    backToTimeBlocks() {
      if (!this.project) {
        this.backToProjects()
        return
      }
      this.view = 'timeblocks'
      this.loadTimeBlocks()
    },

    // ---------------------------------------------------------------- 展示helper
    typeText(type) {
      return WS_TYPE_TEXT[type] || type || '-'
    },
    typeTagType(type) {
      if (type === 'PERSONAL') return 'info'
      if (type === 'ENTERPRISE') return 'warning'
      return ''
    },
    statusText(status) {
      return STATUS_TEXT[status] || status || '-'
    },
    tbTypeText(type) {
      return TB_TYPE_TEXT[type] || type || '-'
    },
    tbStatusText(status) {
      return TB_STATUS_TEXT[status] || status || '-'
    },
    tbStatusTagType(status) {
      if (status === 'DONE' || status === 'COMPLETED') return 'success'
      if (status === 'RUNNING') return 'warning'
      if (status === 'MISSED') return 'danger'
      return 'info'
    },
    resourceTypeText(type) {
      return RESOURCE_TYPE_TEXT[type] || type || '-'
    },
    visibilityText(visibility) {
      return VISIBILITY_TEXT[visibility] || visibility || '-'
    },
    formatTime(value) {
      if (!value) return '-'
      // 后端 LocalDateTime 序列化为 ISO 串（2026-10-08T11:49:28.123）
      return String(value).replace('T', ' ').slice(0, 16)
    },
    /** 输入框里的用户 ID：空 / 非数字一律回 null（后端按 null 跳过该条件） */
    toIdOrNull(raw) {
      const text = String(raw == null ? '' : raw).trim()
      if (!text) return null
      const num = Number(text)
      return Number.isFinite(num) ? num : null
    }
  }
}
</script>

<style scoped>
/* 面包屑：复用 AdminPage 的分隔符 / 当前项类，可点击链接是本面板私有的 */
.wsp-crumb {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
  font-size: 13px;
  color: var(--adm-text-2, rgba(0, 0, 0, 0.65));
}

.wsp-crumb-link {
  color: var(--adm-primary, #1677ff);
  cursor: pointer;
}

.wsp-crumb-link:hover {
  color: var(--adm-primary-hover, #4096ff);
}

/* 小节标题行：左侧标题 + 右侧操作入口（查看评论） */
.wsp-section-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* 时间块详情弹窗底部：左「查看评论」右「关闭」 */
.wsp-dialog-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.wsp-w120 {
  width: 120px;
}

.wsp-w150 {
  width: 150px;
}

.wsp-w220 {
  width: 220px;
}

.wsp-w260 {
  width: 260px;
}
</style>
